// Every URL option of the page (index.html), declared once. The page's own
// parsing (landing.js, store-chrome.js, the quality lab), the floating
// variant switcher (?mock) and the control room (lab.html) all read this
// list: to add an option, add it here, then read it where it takes effect.
//
// An option:
//   key          the query parameter
//   label        a short name, for controls
//   description  one plain sentence: what it does
//   group        the section it is listed under (GROUPS)
//   apply        "live": the page can switch it while running (the switcher
//                and the lab do); "load": it takes effect when the page
//                loads, so changing it reloads the page
//   kind         "choice": one of `values` (absent = `default`); `params`
//                  maps a value to its URL form when that differs (null:
//                  the parameter is absent)
//                "flag": present or absent (true / false)
//                "text": free text checked by `check(text)` (throws on
//                  anything it can't read); absent = null
//                "number": a whole number from `min` to `max`; absent =
//                  `default` (`step`: a control's step; `unit` follows the
//                  number where it is shown)
//   valueLabels  (choice) how a value reads in a control, where not as itself
//   requires     only meaningful with another option set: "tune" (present),
//                "glass=custom", "page=current" or "page=redesign"; set
//                without it, the URL is wrong
//   switcher     listed in the ?mock switcher
//
// A bad value or an unmet `requires` throws: a typo in a link should be
// obvious, not a silent default. Parameters that aren't options are ignored.
import { FADE_AT, HEADER_MODES } from "./variants/header.js";
import { GLASS_KINDS, parseLook } from "./glass/look.js";
import { parseQuality } from "./quality.js";

export const GROUPS = Object.freeze([
  { id: "page", label: "Page" },
  { id: "loading", label: "Loading" },
  { id: "store", label: "Store preview" },
  { id: "render", label: "Rendering" },
  { id: "dev", label: "Developer" },
]);

const GLB = /^\w[\w./-]*\.glb$/;

export const OPTIONS = deepFreeze([
  // js/redesign/: loaded only with redesign, once the scene is shown
  // (landing.js). At load: it builds the page's sections around the hero.
  {
    key: "page", label: "Page", group: "page", apply: "load",
    kind: "choice", values: ["current", "redesign"], default: "current", valueLabels: { redesign: "redesign (windows)" },
    description: "The page as it is, or redesigned as one theme: once the cross is upright the product's text shows beside it, then product photos on white open as a stack of windows, then the store's own sections (the scanning video full screen, the AliRQ video, the photoshoot's stack, the gallery with the drive's files), each photo and video a window like the drive's, with a small bubble at the bottom right (from the text on) as the only add to cart.",
  },
  // js/redesign/cross.js (not imported here: every page would fetch it)
  // lists the modes again; tests/redesign.test.mjs keeps the two in step
  {
    key: "cross", label: "Cross after the opening", group: "page", apply: "live", requires: "page=redesign",
    kind: "choice", values: ["away", "wallpaper", "return"], default: "away",
    valueLabels: { away: "scrolls away", wallpaper: "wallpaper", return: "comes back" },
    description: "On the redesign, what the cross does once the opening is over: scrolls away with it; stays behind the whole page as its wallpaper, turning, every window over it; or scrolls away and comes back, rising in behind the drive's files at the end.",
  },
  // js/redesign/content.js's WHITE_PHOTOS lists them again (not imported
  // here: every page would fetch it); tests/redesign.test.mjs keeps the two
  // in step
  {
    key: "white", label: "Photo on white", group: "page", apply: "load", requires: "page=redesign",
    kind: "choice", values: ["DSC04215", "DSC04219", "DSC04223", "DSC04229", "DSC04205", "DSC04225"], default: "DSC04215",
    description: "On the redesign, which product photo on white is on top of the stack that opens once the cross has scrolled away (on a phone, the one photo shown); the others stay under it in their order.",
  },
  // js/redesign/sections/gallery.js lists them again; tests/redesign.test.mjs keeps the two in step
  {
    key: "gallery", label: "Gallery", group: "page", apply: "live", requires: "page=redesign",
    kind: "choice", values: ["pile", "grid"], default: "pile", valueLabels: { pile: "pile, files behind", grid: "tidy grid" },
    description: "On the redesign, how the gallery looks on a computer: windows left everywhere over the whole screen, as someone who opened a ton of photos left them, each with a close button, the drive's file browser behind them; or a tidy grid of same-size windows, none over another, the file browser after it. On a phone it is one strip to swipe across either way.",
  },
  // js/redesign/sections/gallery.js lists them again (GALLERY_OPENS); tests/redesign.test.mjs keeps the two in step
  {
    key: "galleryopen", label: "Gallery opening", group: "page", apply: "live", requires: "page=redesign",
    kind: "choice", values: ["pop", "browser", "open"], default: "pop", valueLabels: { pop: "windows pop over the files", browser: "files first, then windows", open: "already open" },
    description: "On the redesign, how the gallery's pile opens on a computer: the drive's file browser already there in the pile, the windows popping open around and over it one after another; or the file browser opening first, the drive mounting, then the windows popping open over it as if opened from it; or everything already open when you reach it, nothing opening. On a phone it is one strip either way.",
  },
  // js/redesign/content.js lists them again (FEATURED_PLACES); tests/redesign.test.mjs keeps the two in step
  {
    key: "featuredvideo", label: "Featured video placement", group: "page", apply: "load", requires: "page=redesign",
    kind: "choice", values: ["gallery", "section"], default: "gallery", valueLabels: { gallery: "in the gallery", section: "its own section" },
    description: "On the redesign, where the featured video (AliRQ at Le Bain, its caption in its window's bar) stands: an ordinary window of the gallery, first of its videos; or a section of its own after the scanning video, one window as large as the screen allows. Shown once either way.",
  },
  // js/redesign/sections/media.js lists them again (STACK_LOOKS); tests/redesign.test.mjs keeps the two in step
  {
    key: "stack", label: "Photoshoot stack", group: "page", apply: "live", requires: "page=redesign",
    kind: "choice", values: ["two", "one"], default: "two", valueLabels: { two: "two stacks", one: "one stack" },
    description: "On the redesign, how the photoshoot's 24 photos stand on a computer: two stacks of 12 side by side filling the screen, the red photo on top of the right one (on a screen under 1200 px wide, one stack); or one stack of all 24, the red photo on top. On a phone only the red photo either way.",
  },
  // js/redesign/sections/media.js lists them again (PHONE_STACKS); tests/redesign.test.mjs keeps the two in step
  {
    key: "phonestack", label: "Phone stacks", group: "page", apply: "live", requires: "page=redesign",
    kind: "choice", values: ["single", "flip"], default: "single", valueLabels: { single: "the top photo", flip: "a pile flipping through" },
    description: "On the redesign, how the photos on white and the photoshoot stand on a phone: the top photo alone, plain; or every photo piled exactly on one another in one box, flipping through as you reach them (each landing on the last, quickly, or a step of scroll each with How windows open as you scroll), resting on the top photo.",
  },
  {
    key: "open", label: "How windows open", group: "page", apply: "live", requires: "page=redesign",
    kind: "choice", values: ["scroll", "auto"], default: "auto", valueLabels: { scroll: "as you scroll", auto: "on their own" },
    description: "On the redesign, how a set of windows opens: one after another as you scroll (the grid holds still while its open); or on their own once you reach them, quickly one after another, as a computer restores its windows, and they stay open (the photos on white close again when you scroll back up to the cross, and open again as you come back down).",
  },
  {
    key: "layoutedit", label: "Edit window layout", group: "page", apply: "load", requires: "page=redesign",
    kind: "flag",
    description: "On the redesign, to place the windows by hand: every window stays open (the cross gone), drag one by its title bar, resize it from its bottom-right corner, then Copy layout (a panel, bottom left) copies where every window stands, as JSON for js/redesign/layouts.js.",
  },
  {
    key: "layout", label: "Layout", group: "page", apply: "live", switcher: true, requires: "page=current",
    kind: "choice", values: ["dock", "panel", "desktop", "pill"], default: "dock",
    description: "How the buy control and the way to the drive's files are arranged: a bottom dock, a side panel, desktop icons, or a small pill.",
  },
  {
    key: "buy", label: "Buy", group: "page", apply: "live", switcher: true, requires: "page=current",
    kind: "choice", values: ["a", "b", "c"], default: "a",
    description: "Three presentations of the buy control.",
  },
  {
    key: "glow", label: "Glow", group: "page", apply: "live", switcher: true,
    kind: "choice", values: ["1", "0"], default: "1", valueLabels: { 1: "on", 0: "off" },
    description: "The red LED inside the cross lights up while the drive's files are in view.",
  },
  // js/featured/: loaded only when on, once the scene is shown (landing.js).
  // Not imported here (every page would fetch it): the modes are listed
  // again and featuredlook's shape checked; its knobs and ranges are
  // checked by js/featured/look.js as the photos show, and a bad one is
  // reported on the page (tests/featured.test.mjs keeps the two in step).
  {
    key: "featured", label: "Featured photos", group: "page", apply: "live", switcher: true, requires: "page=current",
    kind: "choice", values: ["off", "windows", "desktop"], default: "off", valueLabels: { windows: "Preview windows", desktop: "photo icons" },
    description: "A few of the drive's photos on the page itself, to compare two mockups: small windows (the drive's) popped up as if just opened, or big photo files lying on the page like desktop icons. Off: the page as it is.",
  },
  {
    key: "featuredlook", label: "Featured photos' look", group: "page", apply: "live",
    kind: "text", check: checkFeaturedLook, example: "size:260,count:5,place:end",
    description: "The featured photos' size, number, scatter, overlap and place that differ from the mockup's own, as knob:value pairs. Easier to drag in the lab's Decide tab.",
  },
  {
    key: "loader", label: "Loader", group: "loading", apply: "load", switcher: true,
    kind: "choice", values: ["classic", "fade", "line", "linelabel"], default: "linelabel",
    valueLabels: { fade: "picture", line: "loading line", linelabel: "line + name" },
    description: "What shows while the 3D scene loads. Line + name (the default): the plain backdrop, and if the scene isn't up within 0.4 s a short thin line, the drive's storage bar, filling with the real progress, with NOTHINGRADIO above it and a small \"loading\" below, then the live scene. Line: the same without the words. Fade (picture): the plain backdrop, the pre-rendered poster if the scene is slow, then the live scene. Classic: a Loading screen.",
  },
  {
    key: "delay", label: "Simulated slow start", group: "loading", apply: "load",
    kind: "choice", values: ["0", "3", "8"], default: "0", valueLabels: { 0: "none", 3: "3 s", 8: "8 s" },
    description: "Holds the 3D scene's downloads back this many seconds, as a slow connection would, to watch what the loader shows meanwhile (the poster, then the live scene).",
  },
  // read by index.html's inline poster script (before any module loads), which
  // falls back to its constant on a bad value; parseOptions reports it
  {
    key: "posterdelay", label: "Poster delay", group: "loading", apply: "load",
    kind: "text", check: (text) => checkMs("posterdelay", text), suggestions: [{ value: "0", label: "at once" }, { value: "700", label: "700 ms (the site's)" }, { value: "2000", label: "2 s" }],
    description: "How long (ms) the page shows only its backdrop before it shows the poster, if the scene's downloads aren't in by then. The site's is 700.",
  },
  {
    key: "postermaxwait", label: "Poster max wait", group: "loading", apply: "load",
    kind: "text", check: (text) => checkMs("postermaxwait", text), suggestions: [{ value: "3500", label: "3.5 s (the site's)" }, { value: "1500", label: "1.5 s" }, { value: "60000", label: "never (60 s)" }],
    description: "How long (ms) the backdrop alone may stand in, downloads in or not, before the poster shows anyway (a slow compile). The site's is 3500.",
  },
  {
    key: "store", label: "Store", group: "store", apply: "load",
    kind: "choice", values: ["off", "chrome", "full"], default: "off",
    params: { off: null, chrome: "", full: "full" },
    valueLabels: { chrome: "header + footer", full: "+ sections below" },
    description: "Shows the page inside a snapshot of the store's own header and footer; the last adds the store's sections below it (recommendations, reviews).",
  },
  {
    key: "header", label: "Store header", group: "store", apply: "live", switcher: true,
    kind: "choice", values: HEADER_MODES, default: "visible",
    description: "In the store preview, when the store's header shows: always, once the visitor scrolls (fade), or once the cross has turned upright (late).",
  },
  {
    key: "headerat", label: "Store header fade point", group: "store", apply: "live",
    kind: "number", ...FADE_AT, unit: "%",
    description: "With the store header on fade: how far into the opening it fades in, in percent of the way to the cross upright. 0 is the first scroll; 100 is when upright, as late.",
  },
  {
    key: "fpscap", label: "Frame cap", group: "render", apply: "live", switcher: true,
    kind: "choice", values: ["60", "30", "off"], default: "off", valueLabels: { off: "none" },
    description: "The most frames a second the 3D scene draws (none: every screen refresh). After a minute without input any cap drops to 30.",
  },
  {
    key: "glass", label: "Glass", group: "render", apply: "live",
    kind: "choice", values: ["auto", ...GLASS_KINDS], default: "auto", valueLabels: { auto: "automatic", mtm: "site (MTM)" },
    description: "The resin's material: automatic (the site's glass where this device draws it fast enough, else the custom glass, decided while the page loads), the site's glass (MeshTransmissionMaterial), or the cheaper custom glass.",
  },
  {
    key: "look", label: "Glass look", group: "render", apply: "live", requires: "glass=custom",
    kind: "text", check: parseLook, example: "frost:0.02,dispersion:0.01",
    description: "The custom glass's look settings that differ from its defaults, as knob:value pairs. Easier to tune in the quality lab's Glass section.",
  },
  {
    key: "fps", label: "Frame rate in title", group: "render", apply: "load",
    kind: "flag",
    description: "Shows the scene's frames per second (and how many of them drew) in the tab's title.",
  },
  {
    key: "tune", label: "Quality lab", group: "dev", apply: "load",
    kind: "text", check: parseQuality, example: "samples:5,distortionMode:baked",
    description: "The quality lab: a panel to judge each rendering setting by eye. The value holds the settings that differ from full quality.",
  },
  {
    key: "aa", label: "Antialiasing", group: "dev", apply: "load", requires: "tune",
    kind: "choice", values: ["1", "0"], default: "1", valueLabels: { 1: "on", 0: "off" },
    description: "Smooths the scene's edges. Off starts the canvas without it, to see what it costs.",
  },
  {
    key: "glb", label: "Model", group: "dev", apply: "load", requires: "tune",
    kind: "text", check: checkGlb,
    suggestions: [
      { value: "perf/lab/cross-50.glb", label: "50% triangles" },
      { value: "perf/lab/cross-25.glb", label: "25% triangles" },
    ],
    description: "Loads another model, such as the simplified ones blender/decimate.py writes (local only).",
  },
  {
    key: "bench", label: "Benchmark", group: "dev", apply: "load",
    kind: "flag",
    description: "Measures how fast this device draws the cross in each glass (about half a minute, the cross held in four poses), then shows the results with a button to copy them.",
  },
  {
    key: "mock", label: "Switcher", group: "dev", apply: "load",
    kind: "flag",
    description: "A small floating switcher on the page for the variants above, for a phone or a link without the lab.",
  },
]);

/** The options by key. */
export const OPTION = Object.freeze(Object.fromEntries(OPTIONS.map((o) => [o.key, o])));

/** The options the ?mock switcher lists. */
export const SWITCHER_OPTIONS = Object.freeze(OPTIONS.filter((o) => o.switcher));

// the longest time a ms option (the poster's timings) takes
export const MAX_MS = 60_000;
function checkMs(key, text) {
  if (!(/^\d+$/.test(text) && Number(text) <= MAX_MS)) throw new Error(`${key}="${text}" is not a time in ms; expected a whole number 0..${MAX_MS}`);
  return text;
}

const FEATURED_LOOK = /^(?:[a-z]+:[\w.]+(?:,[a-z]+:[\w.]+)*)?$/;
function checkFeaturedLook(text) {
  if (!FEATURED_LOOK.test(text)) throw new Error(`featuredlook="${text}" is not knob:value pairs, e.g. size:260,place:end`);
  return text;
}

function checkGlb(text) {
  if (!GLB.test(text)) throw new Error(`glb="${text}" is not a relative path to a .glb`);
  return text;
}

function deepFreeze(value) {
  if (value && typeof value === "object" && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const v of Object.values(value)) deepFreeze(v);
  }
  return value;
}

/** A choice value's URL form: the parameter's value, or null for none. */
function urlForm(option, value) {
  if (option.params && value in option.params) return option.params[value];
  return value;
}

// reads one option's raw parameter (null: absent) into its value
function decode(option, raw) {
  switch (option.kind) {
    case "flag":
      return raw !== null;
    case "text":
      if (raw !== null) option.check(raw);
      return raw;
    case "number": {
      if (raw === null) return option.default;
      const n = Number(raw);
      if (!(/^(0|[1-9]\d*)$/.test(raw) && n >= option.min && n <= option.max)) throw new Error(`${option.key}="${raw}" is not a whole number ${option.min}..${option.max}`);
      return n;
    }
    case "choice": {
      if (raw === null) return option.default;
      const value = option.values.find((v) => urlForm(option, v) === raw);
      if (value === undefined) {
        const forms = option.values.map((v) => urlForm(option, v)).filter((f) => f !== null);
        const expected = option.params ? forms.map((f) => (f === "" ? `?${option.key}` : `?${option.key}=${f}`)).join(" or ") : `one of ${forms.join(", ")}`;
        throw new Error(`Unknown ${option.key}="${raw}"; expected ${expected}`);
      }
      return value;
    }
    default:
      throw new Error(`options: ${option.key} has an unknown kind "${option.kind}"`);
  }
}

/** One option's value from its raw parameter (null: absent), checked alone
 * (not against what it requires). */
export function decodeOption(key, raw) {
  const option = OPTION[key];
  if (!option) throw new Error(`options: no option "${key}"`);
  return decode(option, raw);
}

/** Whether `option`'s requirement holds in `values` (every option's value). */
export function requirementMet(option, values) {
  if (!option.requires) return true;
  const [key, wanted] = option.requires.split("=");
  return wanted === undefined ? values[key] !== null && values[key] !== false : values[key] === wanted;
}

/** Whether `value` is what an absent parameter means for `option`. */
export function isAbsent(option, value) {
  if (option.kind === "flag") return value === false;
  if (option.kind === "text") return value === null;
  if (option.kind === "number") return value === option.default;
  return urlForm(option, value) === null || value === option.default;
}

/** Every option's value in `search` (a query string). Throws on a bad value,
 * and on an option set without the option it requires. */
export function parseOptions(search) {
  const q = new URLSearchParams(search);
  const values = {};
  for (const option of OPTIONS) values[option.key] = decode(option, q.get(option.key));
  for (const option of OPTIONS) {
    if (q.has(option.key) && !requirementMet(option, values)) {
      throw new Error(`${option.key}= needs ${option.requires}`);
    }
  }
  return values;
}

/** Every option's value from `given` ({ key: value }: values, not URL
 * forms), the rest at their defaults: the store's section renders its
 * settings as such an object instead of a query string (js/theme.js).
 * Checked as parseOptions checks a URL: throws on an unknown option, a bad
 * value, and an option given without the one it requires. */
export function optionsFromValues(given) {
  const values = {};
  for (const option of OPTIONS) values[option.key] = decode(option, null);
  for (const [key, value] of Object.entries(given)) {
    const option = OPTION[key];
    if (!option) throw new Error(`options: no option "${key}"`);
    values[key] = decode(option, encode(option, value));
  }
  for (const key of Object.keys(given)) {
    const option = OPTION[key];
    if (!isAbsent(option, values[key]) && !requirementMet(option, values)) throw new Error(`${key} needs ${option.requires}`);
  }
  return values;
}

/** One option's value in `search`, checked as parseOptions checks it (its
 * value, and the option it requires), the others left unread. */
export function readOption(search, key) {
  const option = OPTION[key];
  if (!option) throw new Error(`options: no option "${key}"`);
  const q = new URLSearchParams(search);
  const value = decode(option, q.get(key));
  if (option.requires && q.has(key)) {
    const needed = OPTION[option.requires.split("=")[0]];
    if (!requirementMet(option, { [needed.key]: decode(needed, q.get(needed.key)) })) throw new Error(`${key}= needs ${option.requires}`);
  }
  return value;
}

/** `search` with `changes` ({ key: value }) applied, other parameters kept.
 * A value that means "absent" (a choice's default, a flag off, null text)
 * removes its parameter, so links carry only what differs. Readable:
 * colons, commas and slashes unescaped, flags bare (`?mock`, not `?mock=`). */
export function optionsSearch(search, changes) {
  const q = new URLSearchParams(search);
  for (const [key, value] of Object.entries(changes)) {
    const option = OPTION[key];
    if (!option) throw new Error(`options: no option "${key}"`);
    decode(option, encode(option, value)); // throws on a bad value
    if (isAbsent(option, value)) q.delete(key);
    else q.set(key, encode(option, value));
  }
  const s = q.toString()
    .replace(/%3A/g, ":").replace(/%2C/g, ",").replace(/%2F/g, "/")
    .replace(/=(?=&|$)/g, "");
  return s ? `?${s}` : "";
}

// a value's URL form (null: absent)
function encode(option, value) {
  if (option.kind === "flag") {
    if (typeof value !== "boolean") throw new TypeError(`options: ${option.key} is on or off (true/false), got ${value}`);
    return value ? "" : null;
  }
  if (option.kind === "text") {
    if (value !== null && typeof value !== "string") throw new TypeError(`options: ${option.key} is text or null, got ${value}`);
    return value;
  }
  if (option.kind === "number") {
    if (!Number.isInteger(value)) throw new TypeError(`options: ${option.key} is a whole number, got ${value}`);
    return String(value);
  }
  return urlForm(option, value);
}

/** Replaces `win`'s query string with `search` (no reload) and tells
 * whoever watches the page's options (the lab) with an "optionschange"
 * event on `win`. Every in-page change of the URL's options goes through
 * here. */
export function replaceSearch(win, search) {
  win.history.replaceState(win.history.state, "", `${win.location.pathname}${search}${win.location.hash}`);
  win.dispatchEvent(new win.Event("optionschange"));
}

/** The frame cap a `fpscap` value stands for, in frames a second (0: none). */
export const capFps = (fpscap) => (fpscap === "off" ? 0 : Number(fpscap));
